import { generateRandomColor } from "../utils/general.js";
import { resolveAvatarSrc } from "../../avatars/index.js";

const TEACHER_BADGE_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
</svg>`;

const POPUP_SVG = {
    close: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`,
    users: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`,
};

let activePopup = null;

const peers = new Map();

export const getPeer = (uid) => peers.get(uid) ?? null;

function closePopup() {
    if (activePopup) {
        activePopup.remove();
        activePopup = null;
    }
    document.removeEventListener('click', onClickOutside);
}

function onClickOutside(e) {
    if (activePopup && !activePopup.contains(e.target)) 
        closePopup();
}

function createPopup(players, anchorRect) {
    closePopup();

    const popup = document.createElement('div');
    popup.className = "ls-users-popup";
    popup.innerHTML = `
        <div class="ls-users-popup-header">
            <span class="ls-users-popup-title">
                ${POPUP_SVG.users}
                ${players.length} usuarios
            </span>
            <button class="ls-users-popup-close" title="Cerrar">
                ${POPUP_SVG.close}
            </button>
        </div>
        <div class="ls-users-popup-grid">
            ${players.map((p) => `
                <div class="ls-users-popup-item" data-user="${p.username}">
                    <div class="ls-users-popup-avatar-wrap">
                        <img class="ls-users-popup-avatar" 
                        src="${resolveAvatarSrc(p.avatar)}" 
                        alt="${p.username}"
                        style="--user-color: ${p.color || generateRandomColor()}"/>
                        ${(p.isTeacher) ? `
                            <div class="ls-users-popup-teacher-badge" title="Profesor">
                                ${TEACHER_BADGE_SVG}
                            </div>
                        ` : ""}
                    </div>
                    <span class="ls-users-popup-name">${p.username}</span>
                    ${(p.isTeacher) ? `<span class="ls-users-popup-role">Profesor</span>` : ""}
                </div>
            `).join("")}
        </div>
    `;

    popup.querySelector(".ls-users-popup-close").addEventListener('click', closePopup);

    const popupwidth = 380;
    let left = anchorRect.left + anchorRect.width / 2 - popupwidth / 2;
    left = Math.max(10, Math.min(left, window.innerWidth - popupwidth - 10));

    popup.style.cssText = `
        position: fixed;
        top: ${anchorRect.bottom + 8}px;
        left: ${left}px;
        width: ${popupwidth}px;
        z-index: 10002;
    `
    
    document.body.appendChild(popup);
    activePopup = popup;

    requestAnimationFrame(() => {
        document.addEventListener('click', onClickOutside);
    })
}

export const addPlayerList = () => {
    let users = document.createElement("div");
    users.id = "ls-users";
    users.className = "ls-users-bar";
    users.innerHTML = `
        <style>
            .ls-users-bar {
                display: flex;
                align-items: center;
                gap: 6px;
                padding: 4px 10px;
                overflow-x: visible;
            }

            .ls-user-avatar-wrap {
                position: relative;
                flex-shrink: 0;
                cursor: default;
                transition: transform 0.2s ease;
            }

            .ls-user-avatar-wrap:hover {
                transform: translateY(-2px);
            }

            .ls-user-avatar {
                width: 32px;
                height: 32px;
                border-radius: 50%;
                object-fit: cover;
                border: 2.5px solid transparent;
                background: linear-gradient(white, white) padding-box,
                            var(--user-color) border-box;
                box-shadow: 0 2px 6px rgba(0,0,0,0.15);
                transition: box-shadow 0.2s ease;
            }

            .ls-user-avatar-wrap:hover .ls-user-avatar {
                box-shadow: 0 4px 12px rgba(0,0,0,0.25);
            }

            .ls-user-teacher-badge {
                position: absolute;
                bottom: -2px;
                right: -2px;
                width: 14px;
                height: 14px;
                background: linear-gradient(135deg, #FFD700, #FFA500);
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 1px 3px rgba(0,0,0,0.3);
                border: 1.5px solid white;
            }

            .ls-user-teacher-badge svg {
                width: 9px;
                height: 9px;
                fill: white;
            }

            .ls-user-tooltip {
                position: fixed;
                pointer-events: none;
                z-index: 10003;
                opacity: 0;
                transform: translateY(4px) scale(0.95);
                transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
            }

            .ls-user-tooltip.visible {
                opacity: 1;
                transform: translateY(0) scale(1);
            }

            .ls-user-tooltip-inner {
                background: rgba(30, 30, 40, 0.92);
                backdrop-filter: blur(8px);
                color: white;
                padding: 6px 12px;
                border-radius: 8px;
                font-size: 12px;
                font-weight: 500;
                white-space: nowrap;
                box-shadow: 0 4px 16px rgba(0,0,0,0.2);
            }

            .ls-user-tooltip-role {
                font-size: 10px;
                opacity: 0.7;
                margin-top: 2px;
                text-transform: uppercase;
                letter-spacing: 0.5px;
            }

            .ls-user-count {
                flex-shrink: 0;
                min-width: 32px;
                height: 32px;
                display: flex;
                align-items: center;
                justify-content: center;
                background: rgba(255,255,255,0.2);
                border-radius: 50%;
                font-size: 12px;
                font-weight: 700;
                color: white;
                cursor: default;
                transition: all 0.2s ease;
                border: 2px solid rgba(255,255,255,0.3);
                user-select: none;
            }

            .ls-user-count:hover {
                background: rgba(255,255,255,0.35);
                transform: scale(1.08);
                border-color: rgba(255,255,255,0.5);
            }

            .ls-user-count:active {
                transform: scale(0.95);
            }

            .ls-users-popup {
                background: white;
                border-radius: 16px;
                box-shadow: 0 8px 32px rgba(0,0,0,0.2), 0 2px 8px rgba(0,0,0,0.1);
                overflow: hidden;
                animation: popupIn 0.25s cubic-bezier(0.4, 0, 0.2, 1);
            }

            @keyframes popupIn {
                from { opacity: 0; transform: translateY(-8px) scale(0.96); }
                to { opacity: 1; transform: translateY(0) scale(1); }
            }

            .ls-users-popup-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 12px 16px;
                border-bottom: 1px solid #f0f0f5;
                background: linear-gradient(135deg, #f8f6fc, #f3eefc);
            }
                
            .ls-users-popup-title {
                display: flex;
                align-items: center;
                gap: 8px;
                font-size: 14px;
                font-weight: 700;
                color: #4a3f6b;
            }

            .ls-users-popup-title svg {
                stroke: #855cd6;
            }

            .ls-users-popup-close {
                background: none;
                border: none;
                cursor: pointer;
                padding: 4px;
                border-radius: 6px;
                color: #a89bc7;
                transition: all 0.15s ease;
                display: flex;
                align-items: center;
                justify-content: center;
            }
                
            .ls-users-popup-close:hover {
                background: #f0ebf8;
                color: #855cd6;
            }

            .ls-users-popup-grid {
                display: grid;
                grid-template-columns: repeat(4, 1fr);
                gap: 12px;
                padding: 16px;
                max-height: 320px;
                overflow-y: auto;
            }

            .ls-users-popup-item {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 6px;
                padding: 8px 4px;
                border-radius: 12px;
                transition: background 0.15s ease;
                cursor: default;
            }
            
            .ls-users-popup-item:hover {
                background: #f8f6fc;
            }
                
            .ls-users-popup-avatar-wrap {
                position: relative;
                width: 48px;
                height: 48px;
                flex-shrink: 0;
                cursor: default;
            }

            .ls-users-popup-avatar {
                width: 100%;
                height: 100%;
                border-radius: 50%;
                object-fit: cover;
                border: 2.5px solid transparent;
                background: linear-gradient(white, white) padding-box,
                            var(--user-color) border-box;
                box-shadow: 0 2px 8px rgba(0,0,0,0.12);
            }

            .ls-users-popup-teacher-badge {
                position: absolute;
                bottom: -2px;
                right: -2px;
                width: 16px;
                height: 16px;
                background: linear-gradient(135deg, #FFD700, #FFA500);
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 1px 4px rgba(0,0,0,0.25);
                border: 2px solid white;
            }

            .ls-users-popup-teacher-badge svg {
                width: 10px;
                height: 10px;
                fill: white;
            }

            .ls-users-popup-name {
                font-size: 11px;
                font-weight: 600;
                color: #4a3f6b;
                text-align: center;
                max-width: 100%;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .ls-users-popup-role {
                font-size: 9px;
                font-weight: 700;
                color: #FFA500;
                text-transform: uppercase;
                letter-spacing: 0.5px;
            }
    
        </style>
    `;

    let parent = document.body
        .querySelector(".index_app_ZUgsL")
        ?.querySelector(".box_box_bP3Aq")
        ?.querySelector(".menu-bar_menu-bar_x2Jqi");

    if (parent) {
        parent.insertBefore(users, parent.childNodes[1]);
    }
};

export const updatePlayerCount = (data) => {
    const panel = document.getElementById("ls-users");
    const players = data.players;

    peers.clear();
    for (const player of players || []) {
        if (player?.uid) peers.set(player.uid, player);
    }

    if (!panel) return;

    const styleTag = panel.querySelector("style");
    panel.innerHTML = "";
    if (styleTag) panel.appendChild(styleTag);

    document.querySelectorAll(".ls-user-tooltip, .ls-user-popup").forEach((el) => {
        if (!el.closest(".ls-users-popup")) el.remove();
    });
    if (activePopup) activePopup = null;

    const visiblePlayers = players.slice(0, 4);
    const hiddenCount = players.length - 4;

    visiblePlayers.forEach((player) => {
        const wrap = document.createElement("div");
        wrap.className = "ls-user-avatar-wrap";

        const avatar = document.createElement("img");
        avatar.className = "ls-user-avatar";
        avatar.src = resolveAvatarSrc(player.avatar);
        avatar.style.setProperty("--user-color", player.color || generateRandomColor());
        avatar.alt = player.username;

        if (player.isTeacher) {
            const badge = document.createElement("div");
            badge.className = "ls-user-teacher-badge";
            badge.innerHTML = TEACHER_BADGE_SVG;
            badge.title = "Profesor";
            wrap.appendChild(badge);
        }

        const tooltip = document.createElement("div");
        tooltip.className = "ls-user-tooltip";
        tooltip.id = `ls-user-tooltip-${player.username}`;
        tooltip.innerHTML = `
            <div class="ls-user-tooltip-inner">
                ${player.username}
                ${player.isTeacher ? '<div class="ls-user-tooltip-role">Profesor</div>' : ""}
            </div>
        `;

        document.body.appendChild(tooltip);

        wrap.addEventListener("mouseenter", () => {
            const rect = wrap.getBoundingClientRect();
            tooltip.style.left = `${rect.left + rect.width / 2}px`;
            tooltip.style.top = `${rect.bottom + 8}px`;
            tooltip.style.opacity = 1;
            requestAnimationFrame(() => {
                const tw = tooltip.offsetWidth;
                tooltip.style.left = `${rect.left + rect.width / 2 - tw / 2}px`;
            });
        });

        wrap.addEventListener("mouseleave", () => {
            tooltip.style.opacity = 0;
        });

        wrap.appendChild(avatar);
        panel.appendChild(wrap);
    })

    const count = document.createElement('div');
    count.className = "ls-user-count";
    count.textContent = players.length;
    count.title = `${players.length} usuarios conectados${hiddenCount > 0 ? ` (${hiddenCount} más)` : ""}`;

    if (hiddenCount > 0) {
        count.style.cursor = 'pointer';
        count.addEventListener('click', (e) => {
            e.stopPropagation();
            const rect = count.getBoundingClientRect();
            createPopup(players, rect);
        })
    }

    panel.appendChild(count);
};
